import React, {
  useEffect, useMemo, useCallback, useState
}
from 'react';

import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import './App.css';
import ExtractedValuesPage from './ExtractedValuesPage';
import {getExtractionRuntime} from './extractionRuntime';

const MAIN_LOGO_URL = `${process.env.PUBLIC_URL || ''}/atlas-compass-dark.png`;
const LOGIN_LOGO_URL = `${process.env.PUBLIC_URL || ''}/atlas-logo-dark.png`;

// Primary navigation shown in the simplified administrator sidebar.
const NAV = [['Jobs','▣'],['Prompts','▤'],['Reports','▥'],];

// Use the best available document name supplied by the jobs API.
const getName = job => job.original_filename || job.saved_name || job.job_id || 'Unnamed PDF';
const getCongaDestination = job => {
  const status =
    (job.conga_send_status || '').toLowerCase();

  const labels = {
    pending: 'Conga queued',
    waiting_for_parent_send: 'Waiting for parent',
    sending: 'Sending to Conga…',
    sent: 'Conga ✓',
    failed: 'Send failed',
    error: 'Send failed',
    blocked_parent_send_failed: 'Blocked by parent',
    cancelled: 'Auto-send cancelled',
    not_requested: 'Not sent'
  };

  return {
    status,
    label: labels[status] || 'Not sent'
  };
};
const hasCongaSendIssue = job => [
  'failed',
  'error',
  'blocked_parent_send_failed'
].includes((job.conga_send_status || '').toLowerCase());

const needsAttention = job =>
  ['error', 'failed'].includes(job.status)
  || hasCongaSendIssue(job);

function Brand({login=false}) {
 return <div className="brand"><img src={login?LOGIN_LOGO_URL:MAIN_LOGO_URL} alt="ATLAS" /></div>
}
function Sidebar( {
  active='Jobs',onNavigate=()=> {
  },username='',onLogout,canViewPrompts=false
 }
 ) {
 return <aside className="sidebar"><Brand/><nav> {
  NAV.filter(([name])=>name!=='Prompts'||canViewPrompts).map(([name,icon])=><button key= {
     name
   }
    className= {
     active===name?'active':''
   }
    onClick= {
     ()=>onNavigate(name)
   }
   ><span> {
     icon
   }
   </span> {
     name
   }
   </button>)
 }
 </nav><div className="sidebar-bottom"><div className="secure"><span>◆</span><div><b>Secure workspace</b><small>Contract ingestion</small></div></div><div className="user-card"><i> {
   (username||'AD').slice(0,2).toUpperCase()
 }
 </i><div><b> {
   username||'Administrator'
 }
 </b></div> {
   onLogout&&<button onClick= {
     onLogout
   }
    title="Sign out">↪</button>
 }
 </div></div></aside>
}
function LoginPage({
  onLoginSuccess
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const microsoftLogin = () => {
    window.location.href = '/api/auth/login';
  };

  const submit = async e => {
    e.preventDefault();

    setLoading(true);
    setStatus('');

    try {
      const r = await fetch('/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          username,
          password
        })
      });

      const raw = await r.text();

      let data = {};

      try {
        data = raw
          ? JSON.parse(raw)
          : {};
      } catch {
        throw new Error(
          `Server returned an invalid response (${r.status}).`
        );
      }

      if (!r.ok) {
        throw new Error(
          data.detail
          || data.error
          || 'Login failed.'
        );
      }

      onLoginSuccess({
        username:
          data.username
          || username,
        name:
          data.name
          || data.username
          || username,
        roles:
          data.roles
          || ['Admin'],
        auth_type:
          data.auth_type
          || 'local',
        is_admin:
          Boolean(data.is_admin)
      });

    } catch (err) {
      setStatus(err.message);

    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">

      <div className="login-brand">
        <Brand login />
        <span>
          Government Contracts CLM
        </span>
      </div>

      <div className="login-card">

        <div className="kicker">
          SECURE ACCESS
        </div>

        <h1>
          Welcome to AtlasCCLM
        </h1>

        <p>
          Sign in with your Microsoft account
          or use administrator credentials.
        </p>

        <button
          type="button"
          className="microsoft-login-btn"
          onClick={microsoftLogin}
        >
          <span className="microsoft-icon">
            ⊞
          </span>

          <span>
            Sign in with Microsoft
          </span>
        </button>

        <div className="login-divider">
          <span>
            OR
          </span>
        </div>

        <form
          className="admin-login-form"
          onSubmit={submit}
        >
          <div className="admin-login-heading">
            Administrator Access
          </div>

          <label>
            Username

            <input
              value={username}
              onChange={
                e =>
                  setUsername(
                    e.target.value
                  )
              }
              autoComplete="username"
              required
            />
          </label>

          <label>
            Password

            <input
              type="password"
              value={password}
              onChange={
                e =>
                  setPassword(
                    e.target.value
                  )
              }
              autoComplete="current-password"
              required
            />
          </label>

          <button
            className="primary-btn"
            disabled={loading}
          >
            {
              loading
                ? 'Signing in…'
                : 'Admin Sign In'
            }
          </button>
        </form>

        {
          status
          && (
            <div className="error-banner">
              {status}
            </div>
          )
        }

        <small>
          Authorized users only · Activity may be monitored
        </small>

      </div>

    </div>
  );
}
function DatabasePage( {
 jobId,onBack
}
) {
 const[details,setDetails]=useState(null);
 const[status,setStatus]=useState('Loading database details…');
 useEffect(()=> {
   let cancelled=false;(async()=> {
     try {
       const r=await fetch(`/api/jobs/${jobId}/database`, {
         credentials:'same-origin'
       }
       );const raw=await r.text();let data= {
       };try {
         data=raw?JSON.parse(raw): {
         }
       }
       catch {
         throw new Error(`Invalid server response (${r.status}).`)
       }
       if(!r.ok)throw new Error(data.error||data.detail||'Could not load database details.');if(!cancelled) {
         setDetails(data);setStatus('')
       }
     }
     catch(e) {
       if(!cancelled)setStatus(e.message)
     }
   }
   )();return()=> {
     cancelled=true
   }
 },[jobId]);
 return <div className="app-shell"><Sidebar/><main className="content"><header className="detail-header"><button className="back-btn" onClick= {
   onBack
 }
 >← All jobs</button><h1>Database details</h1><p>Raw stored data for job {
   jobId
 }
 </p></header> {
   status?<div className="notice"> {
     status
   }
   </div>:<pre className="json-viewer"> {
     JSON.stringify(details,null,2)
   }
   </pre>
 }
 </main></div>
}
function StatusBadge( {
 status
}
) {
  const label = {
    done: 'Complete',
    queued: 'Queued',
    queueing: 'Queueing',
    processing: 'Processing',
    retrying: 'Retrying',
    uploading: 'Uploading',
    waiting_for_parent: 'Waiting for parent',
    blocked_parent_failed: 'Blocked by parent',
    error: 'Attention',
    failed: 'Attention',
    cancelled: 'Cancelled'
  }[status] || status;
 return <span className= {
   `status status-${status}`
 }
 ><i/> {
   label
 }
 </span>
}

function formatAttemptStage(stage) {
  if (!stage) {
    return 'Extraction';
  }

  return String(stage)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

function formatAttemptTime(value) {
  if (!value) {
    return '';
  }

  const date = new Date(
    typeof value === 'number'
      ? value * 1000
      : value
  );

  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString();
}

function getJobErrors(job) {
  const attemptErrors = (
    Array.isArray(job.attempts)
      ? job.attempts
      : []
  ).filter(attempt => (
    attempt
    && (
      attempt.status === 'failed'
      || attempt.status === 'error'
      || attempt.error_type
      || attempt.error_message
    )
  ));

  if (attemptErrors.length) {
    return attemptErrors;
  }

  // Supports older jobs created before attempt history existed.
  const legacyMessage = (
    job.error
    || job.error_message
    || ''
  );

  return legacyMessage
    ? [{
        attempt_id: `legacy-${job.job_id}`,
        status: 'failed',
        error_message: legacyMessage,
        completed_at: job.completed_at
      }]
    : [];
}

function JobErrorLog({job, expanded = false}) {
  const errors = getJobErrors(job);

  if (!errors.length) {
    return null;
  }

  return (
    <details
      className="attempt-error-log"
      open={expanded || undefined}
    >
      <summary>
        <span>Error history</span>

        <b>
          {errors.length} {errors.length === 1 ? 'error' : 'errors'}
        </b>
      </summary>

      <div className="attempt-error-list">
        {errors.map((attempt, index) => {
          const runNumber = attempt.run_number;
          const attemptNumber = attempt.attempt_number;
          const maxAttempts = attempt.max_attempts;

          const occurredAt = formatAttemptTime(
            attempt.completed_at
            || attempt.started_at
          );

          return (
            <article
              className="attempt-error-item"
              key={
                attempt.attempt_id
                || `${runNumber || 0}-${attemptNumber || index}`
              }
            >
              <div className="attempt-error-heading">
                <div>
                  <strong>
                    {
                      attemptNumber
                        ? `Run ${runNumber || 1} · Attempt ${attemptNumber}${maxAttempts ? ` of ${maxAttempts}` : ''}`
                        : 'Recorded job error'
                    }
                  </strong>

                  <span>
                    {formatAttemptStage(attempt.failed_stage)}
                    {attempt.error_type
                      ? ` · ${attempt.error_type}`
                      : ''}
                  </span>
                </div>

                <div className="attempt-error-meta">
                  {attempt.retryable === true && (
                    <em className="retryable">
                      Retryable
                    </em>
                  )}

                  {attempt.retryable === false && (
                    <em className="not-retryable">
                      Not retryable
                    </em>
                  )}

                  {occurredAt && (
                    <time>{occurredAt}</time>
                  )}
                </div>
              </div>

              <p>
                {
                  attempt.error_message
                  || 'No error message was recorded.'
                }
              </p>

              {attempt.error_traceback && (
                <details className="attempt-traceback">
                  <summary>Technical details</summary>

                  <pre>{attempt.error_traceback}</pre>
                </details>
              )}
            </article>
          );
        })}
      </div>
    </details>
  );
}

function CongaSendError({job, expanded = false}) {
  if (!hasCongaSendIssue(job)) return null;

  return (
    <details className="attempt-error-log" open={expanded || undefined}>
      <summary><span>Conga send error</span></summary>
      <div className="attempt-error-list">
        <article className="attempt-error-item">
          <p>{job.conga_send_error || 'No Conga error message was recorded.'}</p>
        </article>
      </div>
    </details>
  );
}

function ProcessingStage({job}) {
 const steps=job.steps||{};
 let label='Waiting to begin';
 if(job.status==='queued')label='Waiting in extraction queue';
 else if(job.status==='uploading')label='Uploading PDF';
 else if(job.status==='done')label='Ready for review';
 else if(['error','failed'].includes(job.status))label=String(job.error||job.error_message||'').toLowerCase().includes('upload')?'Upload failed':'Extraction failed';
 else if(job.status==='processing') {
   if(steps.render_all_pages!=='ok')label='Rendering document pages';
   else if(steps.classify_header_form!=='ok')label='Identifying agreement type';
   else if(steps.classify_page_sections!=='ok'||(job.pages_classified||0)<(job.pages_rendered||0))label=`Classifying pages ${job.pages_classified||0} of ${job.pages_rendered||0}`;
   else if(steps.qwen_extract_sections!=='ok'||(job.pages_extracted||0)<(job.pages_rendered||0))label=`Extracting fields ${job.pages_extracted||0} of ${job.pages_rendered||0}`;
   else if(steps.json_build!=='ok')label='Building structured JSON';
   else label='Finalizing extraction';
 }
 return <small className="processing-stage">{label}</small>;
}

function getJobProgress(job) {
  const status = job.status;
  const steps = job.steps || {};

  if (status === 'done') {
    return 100;
  }

  if (['error', 'failed', 'cancelled'].includes(status)) {
    return 0;
  }

  if (status === 'queued') {
    return 5;
  }

  if (status === 'uploading') {
    return 3;
  }

  if (status !== 'processing') {
    return 0;
  }

  // -----------------------------------------
  // Stage 1: Render PDF pages
  // 5% -> 8%
  // -----------------------------------------

  if (steps.render_all_pages !== 'ok') {
    return 8;
  }

  // -----------------------------------------
  // Stage 2: Identify government form
  // 8% -> 12%
  // -----------------------------------------

  if (steps.classify_header_form !== 'ok') {
    return 12;
  }

  // -----------------------------------------
  // Stage 3: Page classification/extraction
  // 12% -> 65%
  //
  // This is calculated from actual completed
  // pages instead of adding a fixed percentage
  // for every page.
  // -----------------------------------------

  const totalPages = Number(
    job.pages_rendered || 0
  );

  const extractedPages = Number(
    job.pages_extracted || 0
  );

  if (
    steps.qwen_extract_sections !== 'ok'
    || (
      totalPages > 0
      && extractedPages < totalPages
    )
  ) {
    const pageRatio =
      totalPages > 0
        ? Math.min(
            1,
            extractedPages / totalPages
          )
        : 0;

    return Math.round(
      12 + (pageRatio * 53)
    );
  }

  // -----------------------------------------
  // Stage 4: Raw extraction JSON
  // -----------------------------------------

  if (steps.raw_json !== 'ok') {
    return 68;
  }

  // -----------------------------------------
  // Stage 5: Resolve identifiers
  // -----------------------------------------

  if (steps.resolve_identifier !== 'ok') {
    return 70;
  }

  // -----------------------------------------
  // Stage 6: Structured JSON
  //
  // This is one of the slower stages, so
  // intentionally reserve a large portion
  // of the progress bar for it.
  // -----------------------------------------

  if (steps.structured_json !== 'ok') {
    return 82;
  }

  // -----------------------------------------
  // Stage 7: Build final JSON payload
  // -----------------------------------------

  if (steps.json_build !== 'ok') {
    return 97;
  }

  // Processing has essentially finished,
  // but don't display 100 until the backend
  // actually marks the job done.
  return 99;
}

function ExtractionRuntime({job}) {
 const runtime=getExtractionRuntime(job);
 if(!runtime)return null;
 return <small className={`runtime runtime-${runtime.tone}`}>{runtime.label}</small>;
}

function PromptsPage({readOnly=false}) {
  const [prompts, setPrompts] = useState([]);
  const [histories, setHistories] = useState({});
  const [expandedKey, setExpandedKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activePromptKey, setActivePromptKey] = useState('');
  const [congaAutoSendEnabled, setCongaAutoSendEnabled] = useState(false);
  const [congaAutoSendLoading, setCongaAutoSendLoading] = useState(true); 
  const [congaAutoSendSaving, setCongaAutoSendSaving] = useState(false);

  useEffect(() => {
    if (prompts.length === 0) {
      setActivePromptKey('');
      return;
    }
  
    const activePromptStillExists = prompts.some(
      prompt => prompt.key === activePromptKey
    );
  
    if (!activePromptStillExists) {
      setActivePromptKey(prompts[0].key);
    }
  }, [prompts, activePromptKey]);

  const readResponse = async response => {
    const raw = await response.text();

    let data = {};

    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = {};
      }
    }

    if (!response.ok) {
      throw new Error(
        data.detail
        || data.error
        || `Request failed (${response.status}).`
      );
    }

    return data;
  };

  const loadPrompts = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        '/api/prompts',
        {
          credentials: 'same-origin'
        }
      );

      const data = await readResponse(response);

      setPrompts(
        (data.prompts || []).map(prompt => ({
          ...prompt,
          changeNote: ''
        }))
      );
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not load prompts.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadCongaAutoSendSetting = async () => {
    setCongaAutoSendLoading(true);
  
    try {
      const response = await fetch(
        '/api/settings/conga-auto-send',
        {
          credentials: 'same-origin'
        }
      );
  
      const data = await readResponse(response);
  
      setCongaAutoSendEnabled(
        Boolean(data.enabled)
      );
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not load the Conga automatic-send setting.'
      );
    } finally {
      setCongaAutoSendLoading(false);
    }
  };

  useEffect(() => {
    loadPrompts();
    loadCongaAutoSendSetting();
  }, []);

  const toggleCongaAutoSend = async () => {
    if(readOnly) {
      return;
    }
    const nextEnabled = !congaAutoSendEnabled;
  
    if (
      !nextEnabled
      && !window.confirm(
        'Turn off automatic Conga sending? '
        + 'Deliveries that have not started will be cancelled.'
      )
    ) {
      return;
    }
  
    setCongaAutoSendSaving(true);
    setMessage('');
    setError('');
  
    try {
      const response = await fetch(
        '/api/settings/conga-auto-send',
        {
          method: 'PUT',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            enabled: nextEnabled
          })
        }
      );
  
      const data = await readResponse(response);
  
      setCongaAutoSendEnabled(
        Boolean(data.enabled)
      );
  
      if (data.enabled) {
        setMessage(
          'Automatic Conga sending is now enabled.'
        );
      } else {
        const cancelledCount =
          Number(data.cancelled_count || 0);
  
        setMessage(
          'Automatic Conga sending is now disabled. '
          + `${cancelledCount} pending `
          + `${cancelledCount === 1 ? 'delivery was' : 'deliveries were'} `
          + 'cancelled.'
        );
      }
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not update automatic Conga sending.'
      );
    } finally {
      setCongaAutoSendSaving(false);
    }
  };

  const updatePrompt = (
    promptKey,
    field,
    value
  ) => {
    setPrompts(items =>
      items.map(item =>
        item.key === promptKey
          ? {
              ...item,
              [field]: value
            }
          : item
      )
    );
  };

  const loadHistory = async promptKey => {
    const response = await fetch(
      `/api/prompts/${encodeURIComponent(
        promptKey
      )}/versions`,
      {
        credentials: 'same-origin'
      }
    );

    const data = await readResponse(response);

    setHistories(current => ({
      ...current,
      [promptKey]: data.versions || []
    }));
  };

  const toggleHistory = async promptKey => {
    setError('');

    if (expandedKey === promptKey) {
      setExpandedKey('');
      return;
    }

    setExpandedKey(promptKey);

    try {
      await loadHistory(promptKey);
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not load prompt history.'
      );
    }
  };

  const restoreVersion = async (
    prompt,
    version
  ) => {
    if(readOnly) {
      return;
    }
    const confirmed = window.confirm(
      `Make version ${version} of `
      + `${prompt.display_name} active?`
    );

    if (!confirmed) {
      return;
    }

    setSavingKey(prompt.key);
    setMessage('');
    setError('');

    try {
      const response = await fetch(
        `/api/prompts/${encodeURIComponent(
          prompt.key
        )}/versions/${version}/restore`,
        {
          method: 'POST',
          credentials: 'same-origin'
        }
      );

      const data = await readResponse(response);

      setPrompts(items =>
        items.map(item =>
          item.key === prompt.key
            ? {
                ...data.prompt,
                changeNote: ''
              }
            : item
        )
      );

      await loadHistory(prompt.key);

      setMessage(
        `${prompt.display_name} version `
        + `${data.prompt.active_version} `
        + 'is now active.'
      );
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not restore prompt.'
      );
    } finally {
      setSavingKey('');
    }
  };

  const savePrompt = async prompt => {
    if(readOnly) {
      return;
    }
    setMessage('');
    setError('');
  
    const trimmedChangeNote =
      (prompt.changeNote || '').trim();
  
    if (!trimmedChangeNote) {
      setError(
        `A change note is required before saving ${prompt.display_name}.`
      );
      return;
    }
  
    setSavingKey(prompt.key);
  
    try {
      const response = await fetch(
        `/api/prompts/${encodeURIComponent(
          prompt.key
        )}`,
        {
          method: 'PUT',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            content: prompt.content,
            change_note: trimmedChangeNote
          })
        }
      );
  
      const data = await readResponse(response);
  
      setPrompts(items =>
        items.map(item =>
          item.key === prompt.key
            ? {
                ...data.prompt,
                changeNote: ''
              }
            : item
        )
      );
  
      if (expandedKey === prompt.key) {
        await loadHistory(prompt.key);
      }
  
      setMessage(
        `${prompt.display_name} saved as version `
        + `${data.prompt.active_version}.`
      );
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not save prompt.'
      );
    } finally {
      setSavingKey('');
    }
  };

  const downloadActivePrompts = async () => {
    if (readOnly) {
      return;
    }
    setMessage('');
    setError('');
  
    try {
      const response = await fetch(
        '/api/prompts',
        {
          method: 'GET',
          credentials: 'same-origin'
        }
      );
  
      const data = await readResponse(response);
      const activePrompts = data.prompts || [];
      const exportedAt = new Date().toISOString();
  
      const promptSections = activePrompts
        .map(prompt => {
          return [
            '='.repeat(80),
            `Prompt: ${prompt.display_name || prompt.key}`,
            `Key: ${prompt.key}`,
            `Active version: ${prompt.active_version}`,
            '='.repeat(80),
            '',
            prompt.content || ''
          ].join('\n');
        })
        .join('\n\n');

      const fileContents = [
        'AtlasCCLM Active Prompts',
        `Exported: ${exportedAt}`,
        `Prompt count: ${activePrompts.length}`,
        '',
        promptSections
      ].join('\n');

      const blob = new Blob(
        [fileContents],
        {
          type: 'text/plain;charset=utf-8'
        }
      );
  
      const downloadUrl =
        URL.createObjectURL(blob);
  
      const downloadLink =
        document.createElement('a');
  
      const fileTimestamp = exportedAt
        .replace(/[:.]/g, '-');
  
      downloadLink.href = downloadUrl;
      downloadLink.download =
        `atlascclm-active-prompts-${fileTimestamp}.txt`;
  
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
  
      URL.revokeObjectURL(downloadUrl);
  
      setMessage(
        `${activePrompts.length} active prompts downloaded.`
      );
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not download active prompts.'
      );
    }
  };

  const removeVersion = async (
    prompt,
    version
  ) => {
    if(readOnly) {
      return;
    }
    const confirmed = window.confirm(
      `Permanently remove version ${version} `
      + `of ${prompt.display_name}?`
    );

    if (!confirmed) {
      return;
    }

    setSavingKey(prompt.key);
    setMessage('');
    setError('');

    try {
      const response = await fetch(
        `/api/prompts/${encodeURIComponent(
          prompt.key
        )}/versions/${version}`,
        {
          method: 'DELETE',
          credentials: 'same-origin'
        }
      );

      await readResponse(response);
      await loadHistory(prompt.key);

      setMessage(
        `${prompt.display_name} version `
        + `${version} was removed.`
      );
    } catch (requestError) {
      setError(
        requestError.message
        || 'Could not remove prompt version.'
      );
    } finally {
      setSavingKey('');
    }
  };

  if (loading) {
    return (
      <section className="workspace-page">
        <div className="prompt-loading">
          Loading prompts...
        </div>
      </section>
    );
  }

  return (
    <section
      className={`workspace-page${readOnly ? ' prompt-view-only' : ''}`}
      onCopyCapture={readOnly ? event => event.preventDefault() : undefined}
      onCutCapture={readOnly ? event => event.preventDefault() : undefined}
      onDragStartCapture={readOnly ? event => event.preventDefault() : undefined}
      onContextMenuCapture={readOnly ? event => event.preventDefault() : undefined}
    >
      <div className="workspace-heading">
        <div>
          <span className="kicker">
            EXTRACTION CONFIGURATION
          </span>

          <h2>Backend prompts</h2>

          <p>
            Edit active extraction instructions and
            manage previous versions.
          </p>
        </div>

        <button
          type="button"
          className="secondary-btn"
          disabled={readOnly || Boolean(savingKey)}
          onClick={readOnly ? undefined : downloadActivePrompts}
        >
          Download active prompts
        </button>
      </div>

      {message && (
        <div className="notice">
          <span>{message}</span>

          <button
            type="button"
            onClick={() => setMessage('')}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="prompt-error">
          <span>{error}</span>

          <button
            type="button"
            onClick={() => setError('')}
          >
            ×
          </button>
        </div>
      )}

      <div className="conga-auto-send-card">
        <div>
          <h3>Conga automatic send</h3>

          <p>
            Automatically sends Conga documents when
            extraction completes. A parent send failure
            blocks its supporting documents.
          </p>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={congaAutoSendEnabled}
          className={
            `conga-auto-send-toggle${
              congaAutoSendEnabled ? ' enabled' : ''
            }`
          }
          disabled={
            readOnly
            || congaAutoSendLoading
            || congaAutoSendSaving
          }
          onClick={
            readOnly
              ? undefined
              : toggleCongaAutoSend
          }
        >
          <span className="conga-toggle-track">
            <span />
          </span>

          <strong>
            {
              congaAutoSendLoading
                ? 'Loading…'
                : congaAutoSendSaving
                  ? 'Saving…'
                  : congaAutoSendEnabled
                    ? 'On'
                    : 'Off'
            }
          </strong>
        </button>
      </div>

      <div className="prompt-layout">
        <div
          className="prompt-tabs"
          role="tablist"
          aria-label="Extraction prompts"
          aria-orientation="vertical"
        >
          {prompts.map(prompt => {
            const isActive =
              activePromptKey === prompt.key;

            return (
              <button
                key={prompt.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={
                  `prompt-tab${isActive ? ' active' : ''}`
                }
                disabled={Boolean(savingKey)}
                onClick={() => {
                  setActivePromptKey(prompt.key);
                  setMessage('');
                  setError('');
                }}
              >
                {prompt.display_name}
              </button>
            );
          })}
        </div>

      <div
        className="prompt-list"
        role="tabpanel"
      >
        {prompts
          .filter(
            prompt => prompt.key === activePromptKey
          )
          .map(prompt => {
            const versions =
              histories[prompt.key] || [];

            const isExpanded =
              expandedKey === prompt.key;

            const isSaving =
              savingKey === prompt.key;

            return (
              <article
                className="prompt-card"
                key={prompt.key}
              >
                <div className="prompt-card-head">
                  <div>
                    <h3>{prompt.display_name}</h3>
                    <p>{prompt.description}</p>
                  </div>

                  <span>
                    Active v{prompt.active_version}
                  </span>
                </div>

                <label>
                  Prompt instructions

                  {readOnly ? (
                    <pre className="prompt-read-only-text">
                      {prompt.content}
                    </pre>
                  ) : (
                    <textarea
                      rows="16"
                      value={prompt.content}
                      disabled={isSaving}
                      onChange={event =>
                        updatePrompt(
                          prompt.key,
                          'content',
                          event.target.value
                        )
                      }
                    />
                  )}
                </label>

                <label>
                  Change note *

                  <input
                    type="text"
                    value={prompt.changeNote || ''}
                    maxLength={500}
                    placeholder="Describe what changed and why"
                    required
                    disabled={readOnly || isSaving}
                    onChange={event =>
                      updatePrompt(
                        prompt.key,
                        'changeNote',
                        event.target.value
                      )
                    }
                  />
                </label>

                <div className="prompt-actions">
                  <button
                    type="button"
                    className="secondary-btn"
                    disabled={isSaving}
                    onClick={() =>
                      toggleHistory(prompt.key)
                    }
                  >
                    {isExpanded
                      ? 'Hide history'
                      : 'Version history'}
                  </button>

                  <button
                    type="button"
                    className="primary-btn"
                    disabled={readOnly || isSaving}
                    onClick={
                      readOnly
                        ? undefined
                        : () => savePrompt(prompt)
                    }
                  >
                    {isSaving
                      ? 'Saving...'
                      : 'Save new version'}
                  </button>
                </div>

                <div className="prompt-meta">
                  <span>
                    Backend key: {prompt.key}
                  </span>

                  <span>
                    Updated by{' '}
                    {prompt.created_by || 'system'}
                  </span>
                </div>

                {isExpanded && (
                  <div className="prompt-history">
                    <h4>Version history</h4>

                    {versions.length === 0 ? (
                      <p>No versions found.</p>
                    ) : (
                      versions.map(version => (
                        <div
                          className="prompt-version"
                          key={version.id}
                        >
                          <div className="prompt-version-head">
                            <div>
                              <strong>
                                Version {version.version}
                              </strong>

                              {version.is_active && (
                                <span className="active-version">
                                  Active
                                </span>
                              )}

                              <small>
                                {version.created_by}
                                {' • '}
                                {version.created_at
                                  ? new Date(
                                      version.created_at
                                    ).toLocaleString()
                                  : ''}
                              </small>
                            </div>

                            {!version.is_active && (
                              <div className="prompt-version-actions">
                                <button
                                  type="button"
                                  className="secondary-btn"
                                  disabled={readOnly || isSaving}
                                  onClick={
                                    readOnly
                                      ? undefined
                                      : () => restoreVersion(
                                          prompt,
                                          version.version
                                        )
                                  }
                                >
                                  Restore
                                </button>

                                {version.version !== 1 && (
                                  <button
                                    type="button"
                                    className="danger-btn"
                                    disabled={readOnly || isSaving}
                                    onClick={
                                      readOnly
                                        ? undefined
                                        : () => removeVersion(
                                            prompt,
                                            version.version
                                          )
                                    }
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            )}
                          </div>

                          {version.change_note && (
                            <p>{version.change_note}</p>
                          )}

                          <details>
                            <summary>
                              View prompt text
                            </summary>

                            <pre>{version.content}</pre>
                          </details>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const formatReportCurrency = (
  value,
  currency = 'USD'
) => new Intl.NumberFormat(
  'en-US',
  {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  }
).format(
  Number(value || 0)
);

const formatReportNumber = value =>
  new Intl.NumberFormat(
    'en-US',
    {
      notation: 'compact',
      maximumFractionDigits: 1
    }
  ).format(
    Number(value || 0)
  );

const formatCostDate = value => {
  if (!value) {
    return '';
  }

  return new Date(
    `${value}T00:00:00Z`
  ).toLocaleDateString(
    [],
    {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    }
  );
};

const formatMetricDate = value => {
  if (!value) {
    return '';
  }

  return new Date(
    value
  ).toLocaleString(
    [],
    {
      month: 'short',
      day: 'numeric',
      hour: 'numeric'
    }
  );
};

const AZURE_COST_RANGES = [
  ['month', 'This month'],
  ['quarter', 'Current quarter'],
  ['ytd', 'Year to date'],
  ['year', 'Last 12 months']
];

const AZURE_TOKEN_RANGES = [
  ['24h', 'Past 24 hours'],
  ['7d', 'Past 7 days'],
  ['30d', 'Past 30 days']
];

const AZURE_CPU_RANGES = [
  ['24h', 'Past 24 hours'],
  ['2d', 'Past 2 days'],
  ['7d', 'Past 7 days'],
  ['30d', 'Past 30 days']
];

function AzureRangeControl({
  label,
  value,
  options,
  disabled,
  onChange
}) {
  return (
    <label className="azure-range-control">
      <span>{label}</span>

      <select
        value={value}
        disabled={disabled}
        onChange={event =>
          onChange(
            event.target.value
          )
        }
      >
        {options.map(
          ([
            optionValue,
            optionLabel
          ]) => (
            <option
              key={optionValue}
              value={optionValue}
            >
              {optionLabel}
            </option>
          )
        )}
      </select>
    </label>
  );
}

function AzureChartMessage({
  message
}) {
  return (
    <div className="azure-chart-message">
      {message}
    </div>
  );
}

function AzureCostChart({
  chart,
  error
}) {
  if (!chart) {
    return (
      <article className="chart-card azure-chart-card">
        <h3>Accumulated costs</h3>

        <AzureChartMessage
          message={
            error ||
            'No Azure cost data is available.'
          }
        />
      </article>
    );
  }

  const hasForecast =
    chart.points?.some(
      point =>
        point.forecast !== null &&
        point.forecast !== undefined
    );

  return (
    <article className="chart-card azure-chart-card">
      <div className="azure-chart-title">
        <div>
        <span>
          ACCUMULATED COSTS ·{' '}
          {(
            chart.range_label ||
            'This month'
          ).toUpperCase()}
        </span>
          <h3>{chart.title}</h3>
        </div>

        <strong>
          {formatReportCurrency(
            chart.current,
            chart.currency
          )}
        </strong>
      </div>

      <p>
        {chart.period_description}
        {hasForecast
          ? ` · Projected ${formatReportCurrency(
              chart.projected,
              chart.currency
            )}`
          : ''}
      </p>

      {chart.points?.length ? (
        <div className="azure-chart-frame">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <AreaChart
              data={chart.points}
              margin={{
                top: 12,
                right: 12,
                left: 2,
                bottom: 2
              }}
            >
              <defs>
                <linearGradient
                  id={`actual-${chart.title}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#42c95a"
                    stopOpacity={0.72}
                  />

                  <stop
                    offset="95%"
                    stopColor="#42c95a"
                    stopOpacity={0.12}
                  />
                </linearGradient>

                <linearGradient
                  id={`forecast-${chart.title}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#a9e7b1"
                    stopOpacity={0.65}
                  />

                  <stop
                    offset="95%"
                    stopColor="#a9e7b1"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#e8edf2"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tickFormatter={formatCostDate}
                minTickGap={28}
                tickLine={false}
                axisLine={false}
              />

              <YAxis
                tickFormatter={value =>
                  formatReportCurrency(
                    value,
                    chart.currency
                  )
                }
                width={58}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                labelFormatter={formatCostDate}
                formatter={(
                  value,
                  name
                ) => [
                  formatReportCurrency(
                    value,
                    chart.currency
                  ),
                  name === 'actual'
                    ? 'Actual'
                    : 'Forecast'
                ]}
              />

              <Legend
                formatter={value =>
                  value === 'actual'
                    ? 'Actual'
                    : 'Forecast'
                }
              />

              <Area
                type="monotone"
                dataKey="actual"
                stroke="#35b84c"
                strokeWidth={2}
                fill={`url(#actual-${chart.title})`}
                connectNulls
                isAnimationActive={false}
              />

              <Area
                type="monotone"
                dataKey="forecast"
                stroke="#8fd99a"
                strokeWidth={2}
                strokeDasharray="5 4"
                fill={`url(#forecast-${chart.title})`}
                connectNulls
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <AzureChartMessage
          message="Azure returned no cost data for this period."
        />
      )}
    </article>
  );
}

function AzureTokenChart({
  chart,
  error
}) {
  if (!chart) {
    return (
      <article className="chart-card azure-chart-card">
        <h3>Total tokens</h3>

        <AzureChartMessage
          message={
            error ||
            'No Azure token data is available.'
          }
        />
      </article>
    );
  }

  return (
    <article className="chart-card azure-chart-card">
      <div className="azure-chart-title">
        <div>
        <span>
          TOKEN USAGE ·{' '}
          {(
            chart.range_label ||
            'Past 7 days'
          ).toUpperCase()}
        </span>
          <h3>{chart.title}</h3>
        </div>

        <strong>
          {formatReportNumber(
            chart.total
          )}
        </strong>
      </div>

      <p>
        {Number(
          chart.total || 0
        ).toLocaleString()} total tokens
      </p>

      {chart.points?.length ? (
        <div className="azure-chart-frame">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <AreaChart
              data={chart.points}
              margin={{
                top: 12,
                right: 12,
                left: 5,
                bottom: 2
              }}
            >
              <defs>
                <linearGradient
                  id="token-gradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#5274df"
                    stopOpacity={0.72}
                  />

                  <stop
                    offset="95%"
                    stopColor="#5274df"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#e8edf2"
                vertical={false}
              />

              <XAxis
                dataKey="timestamp"
                tickFormatter={formatMetricDate}
                minTickGap={38}
                tickLine={false}
                axisLine={false}
              />

              <YAxis
                tickFormatter={formatReportNumber}
                width={50}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                labelFormatter={formatMetricDate}
                formatter={value => [
                  Number(
                    value || 0
                  ).toLocaleString(),
                  'Total tokens'
                ]}
              />

              <Area
                type="monotone"
                dataKey="value"
                name="Total tokens"
                stroke="#5274df"
                strokeWidth={2}
                fill="url(#token-gradient)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <AzureChartMessage
          message="Azure returned no token usage for this period."
        />
      )}
    </article>
  );
}

const mergeCpuSeries = cpu => {
  const rows = new Map();

  for (
    const series of
      cpu?.series || []
  ) {
    for (
      const point of
        series.points || []
    ) {
      const existing =
        rows.get(
          point.timestamp
        ) || {
          timestamp:
            point.timestamp
        };

      existing[
        series.name
      ] = point.value;

      rows.set(
        point.timestamp,
        existing
      );
    }
  }

  return Array.from(
    rows.values()
  ).sort(
    (left, right) =>
      left.timestamp.localeCompare(
        right.timestamp
      )
  );
};

function AzureCpuChart({
  chart,
  error
}) {
  const points =
    mergeCpuSeries(
      chart
    );

  if (!chart) {
    return (
      <article className="chart-card azure-chart-card">
        <h3>Average CPU Percentage</h3>

        <AzureChartMessage
          message={
            error ||
            'No Azure CPU data is available.'
          }
        />
      </article>
    );
  }

  return (
    <article className="chart-card azure-chart-card">
      <div className="azure-chart-title">
        <div>
        <span>
          VM PERFORMANCE ·{' '}
          {(
            chart.range_label ||
            'Past 2 days'
          ).toUpperCase()}
        </span>
          <h3>{chart.title}</h3>
        </div>
      </div>

      <p>
        Atlas development and production virtual machines
      </p>

      {points.length ? (
        <div className="azure-chart-frame">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <AreaChart
              data={points}
              margin={{
                top: 12,
                right: 12,
                left: 0,
                bottom: 2
              }}
            >
              <defs>
                <linearGradient
                  id="dev-cpu-gradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#5274df"
                    stopOpacity={0.52}
                  />

                  <stop
                    offset="95%"
                    stopColor="#5274df"
                    stopOpacity={0.08}
                  />
                </linearGradient>

                <linearGradient
                  id="prod-cpu-gradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#dc3d96"
                    stopOpacity={0.5}
                  />

                  <stop
                    offset="95%"
                    stopColor="#dc3d96"
                    stopOpacity={0.08}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid
                stroke="#e8edf2"
                vertical={false}
              />

              <XAxis
                dataKey="timestamp"
                tickFormatter={formatMetricDate}
                minTickGap={38}
                tickLine={false}
                axisLine={false}
              />

              <YAxis
                tickFormatter={value =>
                  `${Number(
                    value
                  ).toFixed(0)}%`
                }
                width={44}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                labelFormatter={formatMetricDate}
                formatter={(
                  value,
                  name
                ) => [
                  `${Number(
                    value
                  ).toFixed(2)}%`,
                  name
                ]}
              />

              <Legend />

              <Area
                type="monotone"
                dataKey="AtlasCCLM-DevVM"
                stroke="#5274df"
                strokeWidth={2}
                fill="url(#dev-cpu-gradient)"
                connectNulls
                isAnimationActive={false}
              />

              <Area
                type="monotone"
                dataKey="MIOS-Conga-VM"
                stroke="#dc3d96"
                strokeWidth={2}
                fill="url(#prod-cpu-gradient)"
                connectNulls
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <AzureChartMessage
          message="Azure returned no CPU data for this period."
        />
      )}
    </article>
  );
}

function ReportsPage({
  jobs
}) {
  const [
    azureData,
    setAzureData
  ] = useState(null);

  const [
    azureLoading,
    setAzureLoading
  ] = useState(true);

  const [
    azureRefreshing,
    setAzureRefreshing
  ] = useState(false);

  const [
    azureError,
    setAzureError
  ] = useState('');

  const [
    costRange,
    setCostRange
  ] = useState('month');
  
  const [
    tokenRange,
    setTokenRange
  ] = useState('7d');
  
  const [
    cpuRange,
    setCpuRange
  ] = useState('2d');

  const total =
    jobs.length;

  const complete =
    jobs.filter(
      job =>
        job.status === 'done'
    ).length;

  const failed =
    jobs.filter(
      job =>
        [
          'error',
          'failed'
        ].includes(
          job.status
        )
    ).length;

  const processing =
    jobs.filter(
      job =>
        [
          'queued',
          'processing',
          'uploading'
        ].includes(
          job.status
        )
    ).length;

  const successRate =
    total
      ? Math.round(
          complete /
          total *
          100
        )
      : 0;

  const metrics = [
    [
      'Successful',
      complete,
      '#2f8262'
    ],
    [
      'Processing',
      processing,
      '#3c78ad'
    ],
    [
      'Needs attention',
      failed,
      '#b55348'
    ]
  ];

  const loadAzureReports =
    useCallback(
      async ({
        silent = false
      } = {}) => {
        if (silent) {
          setAzureRefreshing(
            true
          );
        } else {
          setAzureLoading(
            true
          );
        }

        try {
          const query =
            new URLSearchParams({
              cost_range:
                costRange,
              token_range:
                tokenRange,
              cpu_range:
                cpuRange
            }).toString();

          const response =
            await fetch(
              `/api/reports/azure?${query}`,
              {
                credentials:
                  'same-origin',
                cache:
                  'no-store'
              }
            );

          const raw =
            await response.text();

          let data = {};

          try {
            data = raw
              ? JSON.parse(
                  raw
                )
              : {};
          } catch {
            throw new Error(
              (
                'Invalid Azure reports '
                + `response (${response.status}).`
              )
            );
          }

          if (!response.ok) {
            throw new Error(
              data.error ||
              data.detail ||
              (
                'Could not load '
                + 'Azure reports.'
              )
            );
          }

          setAzureData(
            data
          );

          setAzureError(
            ''
          );

        } catch (
          error
        ) {
          setAzureError(
            error.message
          );

        } finally {
          setAzureLoading(
            false
          );

          setAzureRefreshing(
            false
          );
        }
      },
      [
        costRange,
        tokenRange,
        cpuRange
      ],
    );

  useEffect(
    () => {
      loadAzureReports();

      const refresh = () => {
        if (
          document.visibilityState
          === 'visible'
        ) {
          loadAzureReports({
            silent: true
          });
        }
      };

      const intervalId =
        window.setInterval(
          refresh,
          300000
        );

      return () => {
        window.clearInterval(
          intervalId
        );
      };
    },
    [
      loadAzureReports
    ]
  );

  const errors =
    azureData?.errors || {};

  return (
    <section className="workspace-page">
      <div className="workspace-heading">
        <div>
          <span className="kicker">
            OPERATIONAL ANALYTICS
          </span>

          <h2>
            Extraction reports
          </h2>

          <p>
            Extraction performance and live Azure operational data.
          </p>
        </div>
      </div>

      <div className="report-metrics">
        <div>
          <span>
            Overall success rate
          </span>

          <strong>
            {successRate}%
          </strong>

          <small>
            {complete} of {total} jobs completed
          </small>
        </div>

        <div>
          <span>
            Total jobs
          </span>

          <strong>
            {total}
          </strong>

          <small>
            All available records
          </small>
        </div>

        <div>
          <span>
            In progress
          </span>

          <strong>
            {processing}
          </strong>

          <small>
            Queued or processing
          </small>
        </div>

        <div>
          <span>
            Needs attention
          </span>

          <strong>
            {failed}
          </strong>

          <small>
            Errors requiring review
          </small>
        </div>
      </div>

      <div className="report-grid">
        <article className="chart-card">
          <h3>
            Job outcomes
          </h3>

          <p>
            Distribution across the current job history
          </p>

          <div className="bar-chart">
            {metrics.map(
              ([
                label,
                value,
                color
              ]) => (
                <div
                  className="bar-row"
                  key={label}
                >
                  <span>
                    {label}
                  </span>

                  <div>
                    <i
                      style={{
                        width: `${
                          total
                            ? Math.max(
                                3,
                                value /
                                total *
                                100
                              )
                            : 0
                        }%`,
                        background:
                          color
                      }}
                    />
                  </div>

                  <b>
                    {value}
                  </b>
                </div>
              )
            )}
          </div>
        </article>

        <article className="chart-card donut-card">
          <h3>
            Completion health
          </h3>

          <p>
            Successful extractions compared with all jobs
          </p>

          <div
            className="donut"
            style={{
              background:
                `conic-gradient(
                  #2f8262 0 ${successRate}%,
                  #e7ecf1 ${successRate}% 100%
                )`
            }}
          >
            <div>
              <strong>
                {successRate}%
              </strong>

              <span>
                success
              </span>
            </div>
          </div>
        </article>
      </div>

      <div className="azure-report-heading">
        <div>
          <span className="kicker">
            AZURE OPERATIONS
          </span>

          <h2>
            Cloud usage and performance
          </h2>

          <p>
            Live data from Azure Cost Management and Azure Monitor.
          </p>
        </div>

        <div className="azure-report-actions">
          {azureData?.generated_at && (
            <small>
              Last requested:{' '}
              {new Date(
                azureData.generated_at
              ).toLocaleString()}
            </small>
          )}

          <button
            type="button"
            className="secondary-btn"
            disabled={
              azureLoading ||
              azureRefreshing
            }
            onClick={() =>
              loadAzureReports({
                silent: true
              })
            }
          >
            {azureRefreshing
              ? 'Refreshing...'
              : '↻ Refresh charts'}
          </button>
        </div>
      </div>

      <div className="azure-range-controls">
        <AzureRangeControl
          label="Cost graphs"
          value={costRange}
          options={
            AZURE_COST_RANGES
          }
          disabled={
            azureLoading ||
            azureRefreshing
          }
          onChange={
            setCostRange
          }
        />

        <AzureRangeControl
          label="Token graph"
          value={tokenRange}
          options={
            AZURE_TOKEN_RANGES
          }
          disabled={
            azureLoading ||
            azureRefreshing
          }
          onChange={
            setTokenRange
          }
        />

        <AzureRangeControl
          label="CPU graph"
          value={cpuRange}
          options={
            AZURE_CPU_RANGES
          }
          disabled={
            azureLoading ||
            azureRefreshing
          }
          onChange={
            setCpuRange
          }
        />
      </div>

      {azureError && (
        <div className="azure-report-error">
          {azureError}
        </div>
      )}

      {azureLoading && !azureData ? (
        <div className="azure-report-loading">
          Loading Azure reports...
        </div>
      ) : (
        <>
          <div className="azure-cost-grid">
            <AzureCostChart
              chart={
                azureData?.costs
                  ?.atlas_dev
              }
              error={
                errors.atlas_dev
              }
            />

            <AzureCostChart
              chart={
                azureData?.costs
                  ?.mios_conga_ai
              }
              error={
                errors.mios_conga_ai
              }
            />

            <AzureCostChart
              chart={
                azureData?.costs
                  ?.atlas
              }
              error={
                errors.atlas
              }
            />
          </div>

          <div className="azure-operations-grid">
            <AzureTokenChart
              chart={
                azureData?.tokens
              }
              error={
                errors.tokens
              }
            />

            <AzureCpuChart
              chart={
                azureData?.cpu
              }
              error={
                errors.cpu
              }
            />
          </div>
        </>
      )}
    </section>
  );
}

function HomePage( {
  username,
  onLogout,
  canEdit,
  canViewPrompts,
  canEditPrompts
}
) {
  const[jobs,setJobs]=useState([]),[files,setFiles]=useState([]),[message,setMessage]=useState(''),[uploading,setUploading]=useState(false),[loading,setLoading]=useState(true),[cancelling,setCancelling]=useState(''),[reprocessing,setReprocessing]=useState(''),[valuesJob,setValuesJob]=useState(''),[dbJob,setDbJob]=useState(''),[query,setQuery]=useState(''),[filter,setFilter]=useState('All'),[section,setSection]=useState('Jobs'),[pageSize,setPageSize]=useState('10'),[jobPage,setJobPage]=useState(0);
  useEffect(() => {
    const restoreView = () => {
      const { atlasView, jobId } = window.history.state || {};

      setValuesJob(
        atlasView === 'review' ? jobId || '' : ''
      );
      setDbJob(
        atlasView === 'database' ? jobId || '' : ''
      );
    };

    restoreView();
    window.addEventListener('popstate', restoreView);

    return () => {
      window.removeEventListener('popstate', restoreView);
    };
  }, []);

  const openView = (atlasView, jobId) => {
    window.history.pushState({ atlasView, jobId }, '');

    setValuesJob(
      atlasView === 'review' ? jobId : ''
    );
    setDbJob(
      atlasView === 'database' ? jobId : ''
    );
  };

  const closeView = () => {
    if (window.history.state?.atlasView) {
      window.history.back();
    } else {
      setValuesJob('');
      setDbJob('');
    }
  };
  const hasActive=useMemo(()=>jobs.some(j=>['queued','processing','uploading'].includes(j.status)),[jobs]);
  const loadJobs=async( {
   silent=false
 }
 = {
 }
 )=> {
   try {
     if(!silent)setLoading(true);
     const r=await fetch('/api/jobs?limit=500', {
       credentials:'same-origin'
     }
     );
     const raw=await r.text();
     let data= {
     };
     try {
       data=raw?JSON.parse(raw): {
       }
     }
     catch {
       throw new Error(`Invalid job response (${r.status}).`)
     }
     if(!r.ok)throw new Error(data.error||data.detail||'Could not load jobs.');
     setJobs(Array.isArray(data.jobs)?data.jobs:[])
   }
   catch(e) {
     if(!silent)setMessage(e.message)
   }
   finally {
     if(!silent)setLoading(false)
   }
 };
  useEffect(() => {
    // Load immediately when the page opens.
    loadJobs();
  }, []);

  useEffect(() => {
    // Poll quickly while jobs are running and more slowly
    // while waiting for a new job from Conga.
    const refreshInterval = hasActive
      ? 2000
      : 10000;

    const refreshJobs = () => {
      // Do not make unnecessary requests while the tab is hidden.
      if (document.visibilityState === 'visible') {
        loadJobs({ silent: true });
      }
    };

    const intervalId = setInterval(
      refreshJobs,
      refreshInterval
    );

    // Refresh immediately when the user returns to the tab.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshJobs();
      }
    };

    window.addEventListener(
      'focus',
      refreshJobs
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      clearInterval(intervalId);

      window.removeEventListener(
        'focus',
        refreshJobs
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [hasActive]);
  const upload=async()=> {
    if(!canEdit) {
      return;
    }
  
    if(!files.length) {
     setMessage('Choose at least one PDF first.');
     return
    }
   const invalid=files.find(f=>!f.name.toLowerCase().endsWith('.pdf'));
   if(invalid) {
     setMessage(`Only PDFs are allowed: ${invalid.name}`);
     return
   }
   setUploading(true);
   setMessage('Uploading files…');
   let failures=0;
   for(const file of files) {
     const body=new FormData();
     body.append('file',file);
     try {
       const r=await fetch('/api/upload', {
         method:'POST',body,credentials:'same-origin'
       }
       );
       const data=await r.json();
       if(!r.ok)throw new Error(data.detail||data.error||`${file.name} upload failed`)
     }
     catch(e) {
       failures++;
       setMessage(e.message)
     }
   }
   setFiles([]);
   setUploading(false);
   const input=document.getElementById('fileInput');
   if(input)input.value='';
   await loadJobs();
   if(!failures)setMessage('Upload submitted successfully.')
 };
  const cancel=async job=> {
    if(!canEdit) {
      return;
    }
  
   if(!window.confirm(`Cancel extraction for ${getName(job)}?`))return;
   try {
     setCancelling(job.job_id);
     const r=await fetch(`/api/jobs/${job.job_id}/cancel`, {
       method:'POST',credentials:'same-origin'
     }
     );
     const data=await r.json();
     if(!r.ok)throw new Error(data.error||data.message||'Could not cancel job');
     setMessage('Job cancelled.');
     await loadJobs( {
       silent:true
     }
     )
   }
   catch(e) {
     setMessage(e.message)
   }
   finally {
     setCancelling('')
   }
 };
 const remove=async job=> {
   if(!canEdit) {
    return;
   }
   if(!window.confirm(`Remove job for ${getName(job)}?`))return;
   try {
     const r=await fetch(`/api/jobs/${job.job_id}`, {
       method:'DELETE',credentials:'same-origin'
     }
     );
     const data=await r.json();
     if(!r.ok)throw new Error(data.message||data.error||'Could not remove job');
     setJobs(x=>x.filter(j=>j.job_id!==job.job_id));
     setMessage('Job removed.')
   }
   catch(e) {
     setMessage(e.message)
   }
 };
 const reprocess=async job=> {
   if(!canEdit) {
     return;
   }
   try {
     setReprocessing(job.job_id);
     setMessage(`Queuing ${getName(job)} for reprocessing…`);
     const response=await fetch(`/api/jobs/${job.job_id}/reprocess`,{method:'POST',credentials:'same-origin'});
     const data=await response.json();
     if(!response.ok)throw new Error(data.error||'Could not reprocess document.');
     setMessage('Document queued for reprocessing.');
     await loadJobs({silent:true});
   }
   catch(error) {
     setMessage(error.message);
   }
   finally {
     setReprocessing('');
   }
 };
  const send=async job=> {
   if(!canEdit) {
     return;
   }
   if(!window.confirm(`Send ${getName(job)} to Conga?`))return;
   try {
     setJobs(x=>x.map(j=>j.job_id===job.job_id? {
       ...j,conga_send_status:'sending'
     }
     :j));
     const r=await fetch(`/api/jobs/${job.job_id}/send-to-conga`, {
       method:'POST',credentials:'same-origin'
     }
     );
     const data=await r.json();
     if (!r.ok) {
      throw new Error(
        data.error ||
        data.detail ||
        'Could not send to Conga'
      );
    }
    
    if (data.job) {
      setJobs(currentJobs =>
        currentJobs.map(currentJob =>
          currentJob.job_id === job.job_id
            ? data.job
            : currentJob
        )
      );
    } else {
      // Supports an older backend during deployment.
      await loadJobs({ silent: true });
    }
    
    setMessage('Successfully sent to Conga.');
   }
   catch (e) {
    setMessage(
      `Conga send failed: ${e.message}`
    );

    await loadJobs({
      silent: true
    });
  }
 };
  const logout=async()=> {
   await fetch('/api/logout', {
     method:'POST',credentials:'same-origin'
   }
   );
   onLogout()
 };
  if(valuesJob)return <ExtractedValuesPage jobId= {
   valuesJob
 }
  jobSummary= {
   jobs.find(job=>job.job_id===valuesJob)
 }
  username= {
   username
 }
  canEdit= {
   canEdit
 }
  canViewPrompts= {
   canViewPrompts
 }
  onLogout= {
   logout
 }
  onBack={closeView}
  onNavigate={
    name => {
      closeView();
      setSection(name);
    }
 }
 />;
 if(dbJob)return <DatabasePage jobId= {
   dbJob
 }
 onBack={closeView}
 />;
 const shown=jobs.filter(j=> {
   const hay=`${getName(j)} ${j.job_id} ${j.batch_id || ''}`.toLowerCase();const statusMatch=filter==='All'||(filter==='Review'?j.status==='done':filter==='Attention'?needsAttention(j):j.status===filter.toLowerCase());return hay.includes(query.toLowerCase())&&statusMatch
 }
 );
 const numericPageSize=pageSize==='all'?Math.max(1,shown.length):Number(pageSize);
 const jobPageCount=Math.max(1,Math.ceil(shown.length/numericPageSize));
 const safeJobPage=Math.min(jobPage,jobPageCount-1);
 const pagedJobs=pageSize==='all'?shown:shown.slice(safeJobPage*numericPageSize,(safeJobPage+1)*numericPageSize);
  const counts=[['Active jobs',jobs.filter(j=>['queued','processing','uploading'].includes(j.status)).length,'Currently processing'],['Ready for review',jobs.filter(j=>j.status==='done').length,'Requires administrator action'],['Sent to Conga',jobs.filter(j=>j.conga_send_status==='sent').length,'Successfully transferred'],['Needs attention',jobs.filter(needsAttention).length,'Check extraction errors']];
  return <div className="app-shell"><Sidebar active= {
   section
 }
  username= {
   username
 }
  canViewPrompts= {
   canViewPrompts
 }
  onLogout= {
   logout
 }
  onNavigate= {
   setSection
 }
 /><main className={`content ${section==='Jobs'?'jobs-content':''}`}><header className="topbar"><div><span>CONTRACT LIFECYCLE MANAGEMENT</span><h1> {
   section
 }
 </h1></div><div><button className="icon-btn" title="Notifications">●</button></div></header> {
   section==='Prompts'?<PromptsPage readOnly={!canEditPrompts}/>:section==='Reports'?<ReportsPage jobs= {
     jobs
   }
   />:<><section className="metrics"> {
     counts.map(([label,value,note])=><div key= {
       label
     }
     ><span> {
       label
     }
     </span><strong> {
       value
     }
     </strong><small> {
       note
     }
     </small></div>)
   }
   </section><section className="upload-panel"><div className="upload-copy"><span className="upload-icon">⇧</span><div><h2>Ingest contract documents</h2><p>Upload one or more PDF files to begin AI extraction.</p></div></div><input id="fileInput" type="file" accept=".pdf,application/pdf" multiple  disabled={!canEdit} onChange= {
     e=>setFiles(Array.from(e.target.files||[]))
   }
   /><label htmlFor="fileInput" className="secondary-btn">Choose PDFs</label><span className="file-count"> {
     files.length?`${files.length} selected`:'No files selected'
   }
   </span><button className="primary-btn" onClick= {
     upload
   }
    disabled={
      !canEdit||uploading
    }
   > {
     uploading?'Uploading…':'Start extraction'
   }
   </button></section> {
     message&&<div className="notice"><span> {
       message
     }
     </span><button onClick= {
       ()=>setMessage('')
     }
     >×</button></div>
   }
   <section className="jobs-panel"><div className="jobs-panel-sticky"><div className="panel-heading"><div><h2>Extraction jobs</h2><p>Review, validate, and transfer extracted contract records.</p></div><button className="secondary-btn" onClick= {
     ()=>loadJobs()
   }
    disabled= {
     loading
   }
   > {
     loading?'Refreshing…':'↻ Refresh'
   }
   </button></div><div className="job-toolbar"><label className="search-box"><span>⌕</span><input value= {
     query
   }
    onChange= {
     e=>setQuery(e.target.value)
   }
    placeholder="Search file, job ID, or batch ID…"/></label><div className="filters"> {
     ['All','Review','Processing','Queued','Attention'].map(x=><button key= {
       x
     }
      className= {
       filter===x?'active':''
     }
      onClick= {
       ()=>setFilter(x)
     }
     > {
       x
     }
     </button>)
   }
   </div></div></div><div className="job-table"><div className="job-row job-head"><span>Document / job</span><span>Batch ID</span><span>Source</span><span>Extraction Status</span><span>Progress</span><span>Extraction time</span><span>Conga Status</span><span>Actions</span></div> {
     !loading&&!shown.length&&<div className="empty-state">No extraction jobs match this view.</div>
   } 
   {
     pagedJobs.map(job => {
      const progress = getJobProgress(job);
    
      const congaDestination =
        getCongaDestination(job);
    
      const records = job.structured?.records;

      const hasDfPayload =
        Array.isArray(records)
        && records.length === 1
        && records[0]?.Object__c === 'DF';

      const hasSendablePayload =
        job.status === 'done'
        || (
          job.status === 'error'
          && hasDfPayload
        );

      const canManuallySend =
        hasSendablePayload
        && ![
          'pending',
          'waiting_for_parent_send',
          'sending',
          'sent'
        ].includes(congaDestination.status)
        && !(
          job.document_role === 'supporting'
          && congaDestination.status
            === 'blocked_parent_send_failed'
        );
    
      return (
        <article
          className="job-row"
          key={job.job_id}
        >
          <button
            type="button"
            className="document-cell document-link"
            disabled={job.status !== 'done'}
            onClick={() => openView('review', job.job_id)}
          >
            <i>PDF</i>
    
            <div>
              <b>
                {getName(job)}
              </b>
    
              <small>
                {job.job_id}
              </small>

              {job.nschen_name && (
                <small title={job.nschen_name}>
                  {job.nschen_name}
                </small>
              )}
            </div>
          </button>

          <span
            className="batch-id-cell"
            title={job.batch_id || 'No batch ID'}
          >
            {job.batch_id || '—'}
          </span>
    
          <span>
            {job.source || 'Manual upload'}
          </span>
    
          <div className="status-stack">
            <StatusBadge status={job.status} />
    
            <ProcessingStage job={job} />
    
            {['error', 'failed'].includes(job.status) && (
              <small
                className="status-error-message"
                title={
                  job.error
                  || job.error_message
                  || 'Extraction requires administrator review.'
                }
              >
                {
                  job.error
                  || job.error_message
                  || 'Extraction requires administrator review.'
                }
              </small>
            )}
          </div>
    
          <div className="progress">
            <div>
              <i
                style={{
                  width: `${progress}%`
                }}
              />
            </div>
    
            <small>
              {progress}%
            </small>
          </div>
    
          <div className="runtime-cell">
            <ExtractionRuntime job={job} />
          </div>
    
          <span
            className={
              `conga-destination ${
                congaDestination.status || 'not-sent'
              }`
            }
            title={
              job.conga_send_error
              || congaDestination.label
            }
          >
            {congaDestination.label}

            {congaDestination.status === 'sent'
              && job.conga_send_completed_at && (
                <small className="conga-sent-time">
                  Sent: {formatAttemptTime(
                    job.conga_send_completed_at
                  )}
                </small>
              )}

          </span>
    
          <div className="row-actions">
            {job.status === 'done' && (
              <button
                className="review-btn"
                onClick={() => openView('review', job.job_id)}
              >
                Review
              </button>
            )}
    
            <button
              title="Database"
              onClick={() => openView('database', job.job_id)}
            >
              ▦
            </button>
    
            {['queued', 'processing'].includes(job.status) && (
              <button
                title="Cancel"
                onClick={
                  canEdit
                    ? () => cancel(job)
                    : undefined
                }
                disabled={
                  !canEdit
                  || cancelling === job.job_id
                }
              >
                ×
              </button>
            )}
    
            {hasSendablePayload && (
              <button
                className={
                  canManuallySend
                    ? ''
                    : 'action-placeholder'
                }
                title={
                  canManuallySend
                    ? 'Send to Conga'
                    : undefined
                }
                disabled={
                  !canEdit
                  || !canManuallySend
                }
                aria-hidden={!canManuallySend}
                tabIndex={
                  canEdit && canManuallySend
                    ? 0
                    : -1
                }
                onClick={
                  canEdit && canManuallySend
                    ? () => send(job)
                    : undefined
                }
              >
                ↗
              </button>
            )}
    
            {['error', 'failed'].includes(job.status)
              && congaDestination.status !== 'sent' && (
              <button
                className="reprocess-btn"
                title="Reprocess document"
                disabled={
                  !canEdit
                  || reprocessing === job.job_id
                }
                onClick={
                  canEdit
                    ? () => reprocess(job)
                    : undefined
                }
              >
                {
                  reprocessing === job.job_id
                    ? 'Retrying…'
                    : 'Reprocess'
                }
              </button>
            )}
    
            {![
              'queued',
              'processing',
              'uploading'
            ].includes(job.status) && (
              <button
                title="Remove"
                className="danger"
                disabled={!canEdit}
                onClick={
                  canEdit
                    ? () => remove(job)
                    : undefined
                }
              >
                ⌫
              </button>
            )}
          </div>
          {['error', 'failed'].includes(job.status) && (
            <JobErrorLog
              job={job}
              expanded={filter === 'Attention'}
            />
          )}
          <CongaSendError
            job={job}
            expanded={filter === 'Attention'}
          />
        </article>
      );
    })
   }
   <div className="job-pagination"><span>Showing {pagedJobs.length} of {shown.length} jobs</span><label>Jobs per page<select value= {
     pageSize
   }
    onChange= {
     event=> {
       setPageSize(event.target.value);
       setJobPage(0)
     }
   }
   ><option value="10">10</option><option value="20">20</option><option value="all">All</option></select></label><div><button disabled= {
     safeJobPage===0||pageSize==='all'
   }
    onClick= {
     ()=>setJobPage(value=>Math.max(0,value-1))
   }
   >← Previous</button><span>Page {safeJobPage+1} of {jobPageCount}</span><button disabled= {
     safeJobPage>=jobPageCount-1||pageSize==='all'
   }
    onClick= {
     ()=>setJobPage(value=>Math.min(jobPageCount-1,value+1))
   }
   >Next →</button></div></div>
   </div></section></>
 }
 </main></div>
}
function App() {
  const [
    checking,
    setChecking
  ] = useState(true);

  const [
    authenticated,
    setAuthenticated
  ] = useState(false);

  const [
    username,
    setUsername
  ] = useState('');

  const [
    displayName,
    setDisplayName
  ] = useState('');

  const [
    roles,
    setRoles
  ] = useState([]);

  const [
    isAdmin,
    setIsAdmin
  ] = useState(false);

  const canEdit = roles.some(
    role => (
      role === 'Admin'
      || role === 'Reviewer'
    )
  );
  
  const canViewPrompts = roles.some(
    role => (
      role === 'Admin'
      || role === 'Read-only'
    )
  );
  
  const canEditPrompts =
    roles.includes('Admin');

  // -----------------------------------------------
  // Check for an existing authenticated session
  // -----------------------------------------------

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(
          '/api/me',
          {
            credentials: 'same-origin'
          }
        );

        const raw = await r.text();

        const data = raw
          ? JSON.parse(raw)
          : {};

        setAuthenticated(
          Boolean(
            data.authenticated
          )
        );

        setUsername(
          data.username
          || ''
        );

        setDisplayName(
          data.name
          || data.username
          || ''
        );

        setRoles(
          Array.isArray(
            data.roles
          )
            ? data.roles
            : []
        );

        setIsAdmin(
          Boolean(
            data.is_admin
          )
        );

      } catch {
        setAuthenticated(
          false
        );

      } finally {
        setChecking(
          false
        );
      }
    })();

  }, []);

  // -----------------------------------------------
  // Log out after the configured idle period
  // -----------------------------------------------

  useEffect(() => {
    if (!authenticated) {
      return undefined;
    }

    const IDLE_TIMEOUT_MS =
      30 * 60 * 1000;

    const ACTIVITY_UPDATE_MS =
      60 * 1000;

    let lastActivity =
      Date.now();

    let lastServerUpdate = 0;
    let timeoutId;
    let loggingOut = false;

    const logoutForInactivity =
      async () => {
        if (loggingOut) {
          return;
        }

        loggingOut = true;

        try {
          await fetch(
            '/api/logout',
            {
              method: 'POST',
              credentials: 'same-origin'
            }
          );

        } finally {
          window.location.assign('/');
        }
      };

    const checkIdleTime = () => {
      const idleTime =
        Date.now()
        - lastActivity;

      const remaining =
        IDLE_TIMEOUT_MS
        - idleTime;

      if (remaining <= 0) {
        logoutForInactivity();
        return;
      }

      clearTimeout(timeoutId);

      timeoutId = setTimeout(
        checkIdleTime,
        remaining
      );
    };

    const reportActivity =
      async () => {
        const now = Date.now();

        if (
          now - lastServerUpdate
          < ACTIVITY_UPDATE_MS
        ) {
          return;
        }

        lastServerUpdate = now;

        try {
          const response = await fetch(
            '/api/activity',
            {
              method: 'POST',
              credentials: 'same-origin'
            }
          );

          if (response.status === 401) {
            logoutForInactivity();
          }

        } catch {
          // Do not immediately log out because
          // of a temporary network failure.
        }
      };

    const recordActivity = () => {
      lastActivity = Date.now();

      checkIdleTime();
      reportActivity();
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState
        !== 'visible'
      ) {
        return;
      }

      // Browser timers may pause while the
      // browser tab is hidden.
      if (
        Date.now() - lastActivity
        >= IDLE_TIMEOUT_MS
      ) {
        logoutForInactivity();
        return;
      }

      recordActivity();
    };

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
    ];

    activityEvents.forEach(
      eventName => {
        window.addEventListener(
          eventName,
          recordActivity,
          {
            passive: true
          }
        );
      }
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    // Set the initial server activity time
    // and begin the browser timeout.
    reportActivity();
    checkIdleTime();

    return () => {
      clearTimeout(timeoutId);

      activityEvents.forEach(
        eventName => {
          window.removeEventListener(
            eventName,
            recordActivity
          );
        }
      );

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };

  }, [authenticated]);

  // The rest of your existing App function
  // remains below the idle timeout effect.

  if (checking) {
    return (
      <div className="loading-screen">
        <Brand />

        <div className="spinner" />

        <p>
          Loading secure workspace…
        </p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <LoginPage
        onLoginSuccess={
          data => {
            setAuthenticated(
              true
            );

            setUsername(
              data.username
              || ''
            );

            setDisplayName(
              data.name
              || data.username
              || ''
            );

            setRoles(
              Array.isArray(
                data.roles
              )
                ? data.roles
                : []
            );

            setIsAdmin(
              Boolean(
                data.is_admin
              )
            );
          }
        }
      />
    );
  }

  return (
    <HomePage
      username={
        displayName
        || username
      }

      canEdit={
        canEdit
      }
      
      canViewPrompts={
        canViewPrompts
      }
      
      canEditPrompts={
        canEditPrompts
      }

      onLogout={
        () => {
          setAuthenticated(
            false
          );

          setUsername(
            ''
          );

          setDisplayName(
            ''
          );

          setRoles(
            []
          );

          setIsAdmin(
            false
          );
        }
      }
    />
  );
}

export default App;
